import type { Key } from "react";

export interface DiffItem {
  item: Key;
  action: "delete" | "add" | "keep";
}

interface DiffPivot {
  value: Key;
  positions: [number, number];
}

type DiffSlice =
  | {
      type: "pivot";
      pivot: Key;
    }
  | {
      type: "difference";
      from: Key[];
      to: Key[];
    };

export const computeDiff = (from: Key[], to: Key[]) => {
  const pivots = findPivots(from, to);
  const slices = extractSlices(from, to, pivots);

  return slices.flatMap((slice) =>
    slice.type === "pivot"
      ? [{ item: slice.pivot, action: "keep" }]
      : [
          ...slice.from.map((key) => ({
            item: key,
            action: "delete",
          })),
          ...slice.to.map((key) => ({
            item: key,
            action: "add",
          })),
        ],
  ) as DiffItem[];
};

const findPivots = (from: Key[], to: Key[]): DiffPivot[] => {
  return to.reduce<{ pivots: DiffPivot[]; lastPosition: number }>(
    (state, key, i) => {
      const position = from.indexOf(key);

      if (position <= state.lastPosition) {
        return state;
      }

      return {
        pivots: [...state.pivots, { value: key, positions: [position, i] } as DiffPivot],
        lastPosition: position,
      };
    },
    {
      pivots: [],
      lastPosition: -1,
    },
  ).pivots;
};

const extractSlices = (from: Key[], to: Key[], pivots: DiffPivot[]): DiffSlice[] => {
  if (pivots.length === 0) {
    return [
      {
        type: "difference",
        from,
        to,
      },
    ];
  }

  return [
    {
      type: "difference",
      from: from.slice(0, pivots[0]!.positions[0]),
      to: to.slice(0, pivots[0]!.positions[1]),
    },
    ...pivots.flatMap((pivot, i) => [
      {
        type: "pivot",
        pivot: pivot.value,
      } as DiffSlice,
      {
        type: "difference",
        from: from.slice(pivot.positions[0] + 1, i < pivots.length - 1 ? pivots[i + 1]!.positions[0] : from.length),
        to: to.slice(pivot.positions[1] + 1, i < pivots.length - 1 ? pivots[i + 1]!.positions[1] : to.length),
      } as DiffSlice,
    ]),
  ];
};
